import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { formatBatchDay } from '@features/dispatch';
import type { components } from '@/types/api.generated';
import { Badge } from '@resource-kit/ui/react';
import { shopifyConsistency, topDemand, type ShopifyConsistency } from '../overviewModel';
import { CardStatus, OverviewCard } from './OverviewCard';

type SalesBridge = components['schemas']['SalesBridge'];

const SHOPIFY_VARIANT = {
  unavailable: 'secondary',
  mismatch: 'destructive',
  consistent: 'success',
} as const satisfies Record<ShopifyConsistency['kind'], string>;

const SHOPIFY_LABEL = {
  unavailable: 'overview.demand.shopify.unavailable',
  consistent: 'overview.demand.shopify.consistent',
} as const;

/** Tomorrow's top products by units, from the dispatch sales bridge. */
export function TomorrowDemandCard({
  tomorrow,
  className,
}: {
  tomorrow: string;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const bridge = useQuery({
    queryKey: queryKeys.dispatch.salesBridge(tomorrow),
    queryFn: () =>
      http.get<SalesBridge>('/api/dispatch/sales-bridge', { params: { date: tomorrow } }),
    staleTime: 60_000,
  });
  const products = bridge.data ? topDemand(bridge.data.products) : [];
  const consistency = bridge.data ? shopifyConsistency(bridge.data) : null;
  return (
    <OverviewCard
      title={t('overview.demand.title', { date: formatBatchDay(tomorrow, i18n.language) })}
      subtitle={t('overview.demand.subtitle')}
      end={
        consistency ? (
          <Badge data-testid="overview-demand-shopify" variant={SHOPIFY_VARIANT[consistency.kind]}>
            {consistency.kind === 'mismatch'
              ? t('overview.demand.shopify.mismatch', { count: consistency.count })
              : t(SHOPIFY_LABEL[consistency.kind])}
          </Badge>
        ) : null
      }
      className={className}
    >
      {bridge.data ? (
        products.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p) => (
              <div key={p.name} className="flex items-baseline justify-between gap-2">
                <span className="truncate">{p.name}</span>
                <span className="font-mono font-semibold">{p.units}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">{t('overview.demand.empty')}</p>
        )
      ) : (
        <CardStatus
          loading={bridge.isPending}
          label={bridge.isPending ? t('overview.loading') : t('overview.loadError')}
        />
      )}
    </OverviewCard>
  );
}
